/**
 * ASC 606 Phase 11g — Unit Delivery Panel (11d UNITS_DELIVERED).
 *
 * Per-POB:
 *   - Append-only delivery events (11d) with cumulative_units +
 *     excluded_units_count (25-36 carve-out) + target_total_units
 *   - "Log Delivery" modal → POST /log-units-delivered (auto-fires
 *     recognition when fulfillment_trigger=EVENT_DELIVERY | USAGE_BASED)
 *   - Inline RecognitionEventsPanel filtered to UNITS_DELIVERED
 *
 * Backend:
 *   GET /contracts/{id}/pobs/{pob_id}/unit-deliveries
 *   POST /contracts/{id}/pobs/{pob_id}/log-units-delivered
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../../constants';
import DataTable, { Column } from './DataTable';
import Modal from './Modal';
import RecognitionEventsPanel from './RecognitionEventsPanel';
import { FormRow, InlineError, inputStyle, btnPrimary, btnSecondary } from './CostLedgerPanel';

const API = `${API_V1}/revenue`;

interface UnitDelivery {
  event_id: number;
  delivery_reference: string | null;
  cumulative_units: number;
  excluded_units_count: number;
  exclusion_reason: string | null;
  target_total_units: number | null;
  unit_of_measure: string;
  delivery_date: string | null;
  delivered_by: number | null;
  source_reference: string | null;
  invoice_amount: number | null;
  invoice_reference: string | null;
  notes: string | null;
  created_at: string | null;
}

interface Props {
  contractId: number;
  pobId: number;
  onStateChanged?: () => void;
}

export default function UnitDeliveryPanel({ contractId, pobId, onStateChanged }: Props) {
  const [events, setEvents] = useState<UnitDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [logOpen, setLogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/pobs/${pobId}/unit-deliveries?limit=500`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [contractId, pobId]);

  useEffect(() => {
    load();
  }, [load]);

  const cols: Column[] = [
    { key: 'delivery_date', label: 'Date', width: 110 },
    { key: 'delivery_reference', label: 'Ref', render: (v: string | null) => v || '-' },
    {
      key: 'cumulative_units',
      label: 'Cum Delivered',
      align: 'right',
      render: (v: number, row: UnitDelivery) => `${v} ${row.unit_of_measure}`,
    },
    {
      key: 'excluded_units_count',
      label: 'Excluded (25-36)',
      align: 'right',
      render: (v: number, row: UnitDelivery) =>
        v > 0 ? (
          <span title={row.exclusion_reason || ''} style={{ color: 'var(--warning)' }}>
            {v} {row.unit_of_measure}
          </span>
        ) : (
          '-'
        ),
    },
    {
      key: 'target_total_units',
      label: 'Target',
      align: 'right',
      render: (v: number | null, row: UnitDelivery) => (v == null ? '-' : `${v} ${row.unit_of_measure}`),
    },
    {
      key: 'invoice_amount',
      label: 'Invoice (R2I)',
      align: 'right',
      render: (v: number | null) => (v == null ? '-' : formatAmount(v)),
    },
    { key: 'source_reference', label: 'Source', render: (v: string | null) => v || '-' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => setLogOpen(true)} style={btnPrimary}>
          Log Units Delivered
        </button>
      </div>

      {error && (
        <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, fontSize: 12, color: 'var(--negative)' }}>
          {error}
        </div>
      )}
      {flash && (
        <div style={{ padding: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid var(--positive)', borderRadius: 4, fontSize: 12, color: 'var(--positive)' }}>
          {flash}
        </div>
      )}

      <div>
        <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
          Delivery Events (append-only)
        </h3>
        <DataTable
          columns={cols}
          rows={events}
          loading={loading}
          emptyMessage="No deliveries logged yet."
          rowKey="event_id"
        />
      </div>

      <RecognitionEventsPanel
        contractId={contractId}
        pobId={pobId}
        filterMethod="UNITS_DELIVERED"
        title="Recognition Events (Units Delivered)"
      />

      <LogDeliveryModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        contractId={contractId}
        pobId={pobId}
        onSaved={async (msg) => {
          setLogOpen(false);
          setFlash(msg);
          await load();
          if (onStateChanged) onStateChanged();
        }}
      />
    </div>
  );
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  contractId: number;
  pobId: number;
  onSaved: (flash: string) => void;
}

function LogDeliveryModal({ open, onClose, contractId, pobId, onSaved }: ModalProps) {
  const [reference, setReference] = useState('');
  const [cumUnits, setCumUnits] = useState('');
  const [excluded, setExcluded] = useState('0');
  const [exclusionReason, setExclusionReason] = useState('');
  const [target, setTarget] = useState('');
  const [uom, setUom] = useState('EACH');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sourceRef, setSourceRef] = useState('');
  const [invoiceAmt, setInvoiceAmt] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null);
    const cum = parseFloat(cumUnits);
    if (!(cum > 0)) {
      setErr('Cumulative units must be > 0.');
      return;
    }
    const excl = parseFloat(excluded) || 0;
    if (excl > 0 && !exclusionReason.trim()) {
      setErr('Exclusion reason required when excluded_units_count > 0.');
      return;
    }
    if (excl > cum) {
      setErr('Excluded units cannot exceed cumulative delivered.');
      return;
    }
    setBusy(true);
    try {
      const body: any = {
        cumulative_units: cum,
        excluded_units_count: excl,
        unit_of_measure: uom,
        delivery_date: date,
      };
      if (reference.trim()) body.delivery_reference = reference.trim();
      if (excl > 0) body.exclusion_reason = exclusionReason.trim();
      if (target) body.target_total_units = parseFloat(target);
      if (sourceRef.trim()) body.source_reference = sourceRef.trim();
      if (invoiceAmt) body.invoice_amount = parseFloat(invoiceAmt);
      if (invoiceRef.trim()) body.invoice_reference = invoiceRef.trim();
      if (notes.trim()) body.notes = notes.trim();
      const res = await authFetch(
        `${API}/contracts/${contractId}/pobs/${pobId}/log-units-delivered`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (res.ok) {
        const data = await res.json();
        const recogStatus = data.recognition?.status || 'recorded';
        const delta = data.recognition?.period_delta;
        onSaved(
          data.idempotency_hit
            ? `Delivery ${reference} already recorded (event ${data.delivery_event_id}).`
            : `Delivery logged. Recognition: ${recogStatus}${delta ? ` — Δ ${formatAmount(delta)}` : ''}.`,
        );
        setReference('');
        setCumUnits('');
        setExcluded('0');
        setExclusionReason('');
        setTarget('');
        setSourceRef('');
        setInvoiceAmt('');
        setInvoiceRef('');
        setNotes('');
      } else {
        const body2 = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body2.detail === 'string' ? body2.detail : body2.detail?.message || JSON.stringify(body2.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Log Units Delivered"
      description="ASC 606-10-25-33 / DART 8.5.5 — cumulative units delivered (net of 25-36 exclusions)."
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={submit} style={btnPrimary} disabled={busy}>
            {busy ? 'Saving...' : 'Log Delivery'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <FormRow label="Delivery Reference (for idempotency)">
          <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} style={inputStyle} placeholder="e.g. GR-8877" />
        </FormRow>
        <FormRow label="Delivery Date">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
        </FormRow>
        <FormRow label="Cumulative Units (total-to-date)">
          <input type="number" value={cumUnits} onChange={(e) => setCumUnits(e.target.value)} style={inputStyle} step="0.0001" />
        </FormRow>
        <FormRow label="Unit of Measure">
          <select value={uom} onChange={(e) => setUom(e.target.value)} style={inputStyle}>
            <option value="EACH">EACH</option>
            <option value="HOUR">HOUR</option>
            <option value="KG">KG</option>
            <option value="LB">LB</option>
            <option value="SQFT">SQFT</option>
            <option value="SQM">SQM</option>
            <option value="M3">M3</option>
            <option value="LICENSE">LICENSE</option>
          </select>
        </FormRow>
        <FormRow label="Target Total Units (denominator)">
          <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} style={inputStyle} step="0.0001" />
        </FormRow>
        <FormRow label="Excluded Units (25-36)">
          <input type="number" value={excluded} onChange={(e) => setExcluded(e.target.value)} style={inputStyle} step="0.0001" />
        </FormRow>
        {parseFloat(excluded) > 0 && (
          <div style={{ gridColumn: '1 / 3' }}>
            <FormRow label="Exclusion Reason (required)">
              <textarea value={exclusionReason} onChange={(e) => setExclusionReason(e.target.value)} style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }} />
            </FormRow>
          </div>
        )}
        <FormRow label="Source Reference">
          <input type="text" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} style={inputStyle} />
        </FormRow>
        <FormRow label="Invoice Amount (55-18 R2I)">
          <input type="number" value={invoiceAmt} onChange={(e) => setInvoiceAmt(e.target.value)} style={inputStyle} step="0.01" />
        </FormRow>
        <FormRow label="Invoice Reference">
          <input type="text" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} style={inputStyle} />
        </FormRow>
        <div style={{ gridColumn: '1 / 3' }}>
          <FormRow label="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: 40, resize: 'vertical' }} />
          </FormRow>
        </div>
      </div>
      {err && <InlineError>{err}</InlineError>}
    </Modal>
  );
}
